import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';
import { AxiosError } from 'axios';

import { debounce, Modal, PageStatus } from '@unified-checkout/design-system';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';

import type { IField } from '@app/dashboard/types/outerLayout/common';
import type {
  ICalculateInstantSettlementFeesPayload,
  IInstantSettlementResponse,
  ISettlementBalance,
  ISubmitInstantSettlementPayload,
  TBalanceType,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/instant-settlement';
import {
  BALANCE_FIELDS,
  BALANCE_FORM_FIELDS,
  DEBOUNCED_TIME,
  INSTANT_SETTLEMENT_BALANCE_TYPES,
} from '@app/dashboard/constants/balance_and_transfers/balance/modals';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  calculateInstantSettlementFees,
  getBalanceTypes,
  submitInstantSettlement,
} from '@app/dashboard/services/inner_layout/balance_and_transfers/instant-settlement';
import { notify, useNotificationStore } from '@app/dashboard/utils/notifier';
import { useLangStore } from '@app/dashboard/store/lang';
import { convertAmountToCents } from '@app/dashboard/utils/convertAmounts';
import { TWO_SECONDS } from '@app/dashboard/constants/shared';
import { NOTIFICATION_STATUS } from '@app/dashboard/constants/notifier';
import { SUCCESS_STATUS_CODE } from '@app/dashboard/constants/balance_and_transfers/balance';
import { BALANCE_TRANSFERS_EVENTS } from '@app/dashboard/constants/tracking_events/balance-and-transfers';

import { handleStoreLastAction } from '../../../../../utils/events-tracking';
import { getSettlementLimits } from '../../../../../utils';
import ItemSelector from './ItemSelector';
import Banner from './Banner';
import AmountInput from './AmountInput';
import Destination from './Destination';

import styles from './instant-settlement.module.scss';

const InstantSettlement = ({
  isOpen,
  handleClose,
}: {
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const cacheRef = useRef<{ [key: string]: any }>({});
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { lang } = useLangStore();
  const { show: isNotifyShown, state: notifyState } = useNotificationStore();

  const [selectedBalanceType, setSelectedBalanceType] = useState(
    INSTANT_SETTLEMENT_BALANCE_TYPES.AVAILABLE as TBalanceType
  );
  const [fields, setFields] = useState(
    () => BALANCE_FIELDS[BALANCE_FORM_FIELDS.AMOUNT] as unknown as IField
  );

  const toPendingBalanceEventTracking = () => {
    const eventName =
      BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.CHANGE_SETTLEMENT_TYPE_TO_PENDING;

    amplitude.track(eventName);
    handleStoreLastAction(eventName);
  };

  const {
    data: instantSettlementTypes,
    isFetching: isLoadingBalanceTypes,
    isError: isInstantSettlementTypesError,
    error: instantSettlementTypesError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_INSTANT_SETTLEMENT_BALANCE, lang],
    queryFn: getBalanceTypes,
    enabled: isOpen,
    select: (data) => data.data?.data as IInstantSettlementResponse,
    staleTime: Infinity,
  });

  const {
    data: settlementData,
    isPending: isSettlementBalanceFeesPending,
    mutate: getInstantSettlementFees,
    reset: resetSettlementFees,
  } = useMutation({
    mutationKey: [QUERY_KEYS.GET_INSTANT_SETTLEMENT_FEES, lang],
    mutationFn: async ({
      amount_pound = 0,
      settlement_balance,
    }: ICalculateInstantSettlementFeesPayload) => {
      const key = `${amount_pound}-${settlement_balance}`;

      if (key in cacheRef.current) {
        return cacheRef.current[key];
      }

      const response = await calculateInstantSettlementFees({
        amount_pound,
        settlement_balance,
      });

      cacheRef.current[key] = response;

      return response;
    },
    onSuccess: (response) => {
      if (response.data?.data) {
        const { data } = response.data;

        if (data.settlement_fee >= data.amount_pound) {
          setFields((prevFields) => ({
            ...prevFields,
            [BALANCE_FORM_FIELDS.AMOUNT]: {
              ...prevFields[BALANCE_FORM_FIELDS.AMOUNT],
              errorMsg: t(
                'innerLayout.balanceTransfers.balance.instantSettlement.errors.feeExceedsAmount'
              ),
              isValid: false,
            },
          }));
        }
      }
    },

    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('errors.somethingWentWrong');

      notify.error(errorMessage);
    },
  });

  const {
    isPending: isSettlementSubmitPending,
    mutate: mutateSubmitInstantSettlement,
  } = useMutation({
    mutationFn: (payload: ISubmitInstantSettlementPayload) =>
      submitInstantSettlement(payload),
    onSuccess: (response) => {
      amplitude.track(
        BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.SETTLE_NOW_POPUP_CONFIRMED
      );

      if (selectedBalanceType === INSTANT_SETTLEMENT_BALANCE_TYPES.PENDING) {
        toPendingBalanceEventTracking();
      }

      if (response.status === SUCCESS_STATUS_CODE) {
        notify.success(
          t(
            'innerLayout.balanceTransfers.balance.instantSettlement.successMessage'
          )
        );

        setTimeout(() => {
          handleClose();
          setFields((prevFields) => ({
            ...prevFields,
            [BALANCE_FORM_FIELDS.AMOUNT]: {
              ...prevFields[BALANCE_FORM_FIELDS.AMOUNT],
              value: '',
              isValid: false,
              errorMsg: '',
            },
          }));
          resetSettlementFees();
          const queriesToInvalidate = [
            QUERY_KEYS.GET_BALANCE_DETAILS,
            QUERY_KEYS.GET_TRANSFERS,
            QUERY_KEYS.GET_BANK_ACCOUNT_DETAILS,
            QUERY_KEYS.GET_INSTANT_SETTLEMENT_BALANCE,
          ];
          queryClient.invalidateQueries({
            predicate: (query) => {
              return query.queryKey.some((key) =>
                queriesToInvalidate.includes(key as string)
              );
            },
          });
        }, TWO_SECONDS);
      } else {
        notify.error(
          t(
            'innerLayout.balanceTransfers.balance.instantSettlement.errors.submitFailed'
          )
        );
      }
    },

    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('errors.somethingWentWrong');

      notify.error(errorMessage);
    },
  });

  const isLoading = isLoadingBalanceTypes || isSettlementBalanceFeesPending;

  const shouldDisabledSubmitBtn =
    (isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS) ||
    isLoading ||
    !settlementData ||
    isSettlementSubmitPending ||
    isInstantSettlementTypesError ||
    !!fields[BALANCE_FORM_FIELDS.AMOUNT]?.errorMsg ||
    !fields[BALANCE_FORM_FIELDS.AMOUNT]?.value;

  const settlementBalanceFees = useMemo(() => {
    return settlementData?.data?.data;
  }, [settlementData]);

  const debouncedGetFees = useCallback(
    debounce(
      (
        amount: number,
        settlementBalance: TBalanceType,
        error: string | undefined
      ) => {
        const numericValue = Number(amount);

        if (!error) {
          getInstantSettlementFees({
            amount_pound: numericValue,
            settlement_balance: settlementBalance,
          });
        }
      },
      DEBOUNCED_TIME
    ),
    [getInstantSettlementFees]
  );

  const selectedBalance = useMemo(() => {
    return instantSettlementTypes?.balance.find(
      (balance) => balance.balance_type === selectedBalanceType
    );
  }, [instantSettlementTypes, selectedBalanceType]);

  const handleSettle = () => {
    const amount = fields[BALANCE_FORM_FIELDS.AMOUNT]?.value || '';
    if (!amount || !selectedBalance) return;

    const enteredValue = Number(amount);
    const currentBalance = selectedBalance?.balance || 0;
    const currency = selectedBalance?.currency || '';

    const partialSettlement = enteredValue < currentBalance;
    const amountCents = convertAmountToCents(enteredValue, currency, true);

    mutateSubmitInstantSettlement({
      amount_cents: amountCents,
      settlement_balance: selectedBalanceType,
      partial_settlement: partialSettlement,
    });
  };

  const handleBalanceTypeChange = (
    balanceType: TBalanceType,
    balance: ISettlementBalance
  ) => {
    if (balanceType === INSTANT_SETTLEMENT_BALANCE_TYPES.PENDING) {
      toPendingBalanceEventTracking();
    }

    if (
      balanceType === INSTANT_SETTLEMENT_BALANCE_TYPES.AVAILABLE &&
      balance?.allow_partial_settlement
    ) {
      resetSettlementFees();
    }

    setSelectedBalanceType(balanceType);
  };

  const handleValidateAmount = (targetValue: string) => {
    let errorMsg = '';

    const minimumSettlement =
      instantSettlementTypes?.settlement_balance_min_limit || 0;
    const maximumSettlement =
      instantSettlementTypes?.settlement_balance_max_limit || 0;
    const availableBalance = selectedBalance?.balance || 0;

    const {
      isMaxAvailableSettlement,
      isAboveMaxAllowedSettlement,
      isBelowMinAllowedSettlement,
    } = getSettlementLimits({
      targetValue,
      settlementBalanceMaxLimit: maximumSettlement,
      settlementBalanceMinLimit: minimumSettlement,
      selectedBalance: availableBalance,
    });

    if (!targetValue) {
      errorMsg = t('errors.required');
    } else if (isMaxAvailableSettlement) {
      errorMsg = t(
        'innerLayout.balanceTransfers.balance.instantSettlement.errors.amountExceedsAvailable'
      );
    } else if (isBelowMinAllowedSettlement) {
      errorMsg = t(
        'innerLayout.balanceTransfers.balance.instantSettlement.errors.invalidAmount'
      );
    } else if (isAboveMaxAllowedSettlement) {
      errorMsg = t(
        'innerLayout.balanceTransfers.balance.instantSettlement.errors.amountExceedsMax'
      );
    }

    return errorMsg;
  };

  const handleFieldChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    const targetValue = e.target.value || '';
    const errorMsg = handleValidateAmount(targetValue);

    setFields((prevFields) => ({
      ...prevFields,
      [field]: {
        ...prevFields[field],
        value: targetValue,
        isValid: !errorMsg,
        errorMsg,
      },
    }));

    debouncedGetFees(targetValue, selectedBalanceType, errorMsg);
  };

  useEffect(() => {
    setFields((prevFields) => ({
      ...prevFields,
      [BALANCE_FORM_FIELDS.AMOUNT]: {
        ...prevFields[BALANCE_FORM_FIELDS.AMOUNT],
        value: '',
        isValid: false,
        errorMsg: '',
      },
    }));
  }, [selectedBalanceType, lang]);

  useEffect(() => {
    if (
      isOpen &&
      selectedBalance &&
      !selectedBalance.allow_partial_settlement
    ) {
      handleFieldChange(
        {
          target: { value: selectedBalance.balance.toString() },
        } as ChangeEvent<HTMLInputElement>,
        BALANCE_FORM_FIELDS.AMOUNT
      );
    }
  }, [isOpen, selectedBalance]);

  return (
    <Modal
      open={isOpen}
      position="center"
      title={t('innerLayout.balanceTransfers.balance.instantSettlement.title')}
      closeOnClickOutside
      onCloseHandler={handleClose}
      isOverlayAboveModal
      hasCloseIcon
      wrapperClassName={styles['instant-settlement']}
      btnsProps={
        isInstantSettlementTypesError
          ? []
          : [
              {
                text: t(
                  'innerLayout.balanceTransfers.balance.instantSettlement.settleNow'
                ),
                variant: 'contained',
                onClickHandler: handleSettle,
                loading: isSettlementSubmitPending,
                disabled: shouldDisabledSubmitBtn,
              },
              {
                text: t('innerLayout.cancel'),
                variant: 'outlined',
                onClickHandler: handleClose,
                color: 'secondary',
                disabled: isLoading || isSettlementSubmitPending,
              },
            ]
      }
    >
      {isInstantSettlementTypesError ? (
        <PageStatus
          status="error"
          title={t('innerLayout.errors.smthWentWrong')}
          description={
            (instantSettlementTypesError as AxiosError<{ message: string }>)
              ?.response?.data?.message ||
            t('innerLayout.errors.dataRetrievalErr')
          }
          icon={CircleExclamation}
        />
      ) : (
        <div
          className={styles['instant-settlement__body']}
          data-installment-submit-loading={isSettlementSubmitPending}
        >
          <Banner />
          <ItemSelector
            isLoading={isLoading}
            selectedBalanceType={selectedBalanceType}
            handleBalanceTypeChange={handleBalanceTypeChange}
            balances={instantSettlementTypes?.balance as ISettlementBalance[]}
          />
          <AmountInput
            isGetBalanceTypesLoading={isLoadingBalanceTypes}
            isFeesOrSubmitLoading={isSettlementBalanceFeesPending}
            fields={fields}
            handleFieldChange={handleFieldChange}
            selectedBalance={selectedBalance as ISettlementBalance}
            selectedBalanceType={selectedBalanceType}
          />
          {(!!settlementBalanceFees || isSettlementBalanceFeesPending) && (
            <Destination
              isLoading={isLoading}
              settlementBalanceFees={settlementBalanceFees}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default InstantSettlement;
